import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Copy, Download, Phone, Instagram, Crown, Shield, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import LanguageToggle from "../components/LanguageToggle";

const STATUS_TABS = [
    { key: "active", label: "All (excl. rejected)" },
    { key: "approved", label: "Approved" },
    { key: "vip", label: "VIP" },
    { key: "pending", label: "Pending" },
    { key: "rejected", label: "Rejected" },
    { key: "blacklist", label: "Blacklist" },
];

const STATUS_STYLE = {
    pending: "text-amber-300",
    approved: "text-emerald-300",
    vip: "text-gold",
    rejected: "text-red-300",
    blacklist: "text-zinc-400",
};

export default function AdminDirectory() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("active");
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!localStorage.getItem("sc_admin_token")) {
            navigate("/admin/login", { replace: true });
        }
    }, [navigate]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 5000, status: tab };
            if (q.trim()) params.q = q.trim();
            const { data } = await api.get("/admin/applications", { params });
            setItems(data);
        } catch (e) {
            toast.error("Failed to load directory");
        } finally {
            setLoading(false);
        }
    }, [tab, q]);

    useEffect(() => {
        const id = setTimeout(load, 200);
        return () => clearTimeout(id);
    }, [load]);

    const copy = async (text, label = "Copied") => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label}: ${text}`);
        } catch {
            toast.error("Copy failed");
        }
    };

    const copyAllPhones = async (separator = "\n") => {
        const phones = items
            .map((m) => (m.phone || "").trim())
            .filter((p) => p.length >= 4);
        if (phones.length === 0) {
            toast.error("No phone numbers to copy.");
            return;
        }
        const text = phones.join(separator);
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`Copied ${phones.length} phone number${phones.length === 1 ? "" : "s"}`);
        } catch {
            toast.error("Copy failed");
        }
    };

    const exportCsv = () => {
        const header = ["First name", "Last name", "Phone", "Instagram", "Status", "VIP", "Visits", "Created"];
        const rows = items.map((m) => [
            m.first_name || "",
            m.last_name || "",
            m.phone || "",
            m.instagram || "",
            m.status || "",
            m.vip ? "yes" : "",
            m.check_ins ?? 0,
            m.created_at || "",
        ]);
        const csv = [header, ...rows]
            .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const now = new Date().toISOString().slice(0, 10);
        a.href = url;
        a.download = `scottsclub-directory-${tab}-${now}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const counts = useMemo(() => ({
        total: items.length,
        withPhone: items.filter((m) => (m.phone || "").length > 4).length,
    }), [items]);

    return (
        <div className="min-h-[100dvh] text-ink-primary" data-testid="directory-page">
            {/* Header */}
            <header className="sticky top-0 z-30 glass-strong border-b border-emerald-line/60">
                <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/admin" className="inline-flex items-center gap-2 text-ink-secondary hover:text-gold text-xs tracking-[0.22em] uppercase" data-testid="back-admin">
                        <ArrowLeft size={14} /> Admin
                    </Link>
                    <div className="flex items-center gap-3 font-serif text-lg">
                        <Shield size={16} className="text-gold" /> Directory
                    </div>
                    <LanguageToggle />
                </div>
                <div className="gold-divider opacity-30" />
            </header>

            <main className="max-w-5xl mx-auto px-6 py-10">
                <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
                    <div>
                        <div className="label-eyebrow flex items-center gap-2">
                            <Shield size={12} className="text-gold" /> Admin-only phone list
                        </div>
                        <h1 className="font-serif font-medium text-3xl md:text-4xl mt-3">Member directory</h1>
                        <p className="text-ink-secondary mt-3 max-w-xl text-sm">
                            All applicants and members with phone numbers. Not visible to guests or door staff.
                        </p>
                        <p className="text-[11px] text-ink-muted mt-2">
                            Showing {counts.total} record{counts.total === 1 ? "" : "s"} · {counts.withPhone} with a phone.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={() => copyAllPhones("\n")}
                            disabled={items.length === 0}
                            data-testid="copy-all-phones"
                            title="Copy every phone number in the filtered list to your clipboard (one per line)."
                            className="btn-gold inline-flex items-center gap-2 px-4 py-2 rounded-sm font-medium text-[11px] tracking-[0.22em] uppercase disabled:opacity-40"
                        >
                            <ClipboardList size={13} /> Copy all phones
                        </button>
                        <button
                            onClick={() => copyAllPhones(", ")}
                            disabled={items.length === 0}
                            data-testid="copy-all-phones-comma"
                            title="Same list, comma-separated (handy for pasting into an SMS bulk-send field)."
                            className="inline-flex items-center gap-2 px-3 py-2 border border-gold/40 text-gold rounded-sm hover:bg-gold/10 text-[11px] tracking-[0.22em] uppercase disabled:opacity-40"
                        >
                            <Copy size={13} /> Comma list
                        </button>
                        <button
                            onClick={exportCsv}
                            disabled={items.length === 0}
                            data-testid="export-csv"
                            className="inline-flex items-center gap-2 px-3 py-2 border border-emerald-line/60 text-ink-secondary hover:text-gold hover:border-gold/40 rounded-sm text-[11px] tracking-[0.22em] uppercase disabled:opacity-40"
                        >
                            <Download size={13} /> CSV
                        </button>
                    </div>
                </div>

                {/* Tabs + search */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                    <div className="flex flex-wrap gap-1 border border-emerald-line/60 rounded-sm p-1 bg-bg-secondary/40">
                        {STATUS_TABS.map((s) => (
                            <button
                                key={s.key}
                                onClick={() => setTab(s.key)}
                                data-testid={`dir-tab-${s.key}`}
                                className={`px-3 py-1.5 text-[11px] tracking-[0.22em] uppercase rounded-sm transition-colors ${
                                    tab === s.key ? "bg-gold text-black" : "text-ink-secondary hover:text-gold"
                                }`}
                            >{s.label}</button>
                        ))}
                    </div>
                    <div className="relative md:w-80">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search name, phone, IG…"
                            data-testid="dir-search"
                            className="w-full pl-9 pr-3 py-2 bg-bg-secondary/70 border border-emerald-line/60 rounded-sm text-sm focus:border-gold/60 outline-none"
                        />
                    </div>
                </div>

                {/* Directory table */}
                <div className="rounded-sm border border-emerald-line/60 overflow-hidden bg-bg-secondary/40">
                    {loading && items.length === 0 ? (
                        <div className="p-10 text-center text-ink-muted text-sm">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="p-10 text-center text-ink-muted text-sm">No matches.</div>
                    ) : (
                        <ul className="divide-y divide-emerald-line/60" data-testid="dir-list">
                            {items.map((m) => (
                                <li key={m.id} className="px-4 md:px-5 py-3 flex items-center gap-4" data-testid={`dir-row-${m.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <div className="font-medium text-ink-primary truncate">
                                                {m.full_name || `${m.first_name || ""} ${m.last_name || ""}`.trim() || "—"}
                                            </div>
                                            {m.vip && <Crown size={12} className="text-gold flex-shrink-0" />}
                                            <span className={`text-[10px] tracking-[0.18em] uppercase ${STATUS_STYLE[m.status] || "text-ink-muted"}`}>
                                                {m.status}
                                            </span>
                                        </div>
                                        {m.instagram && (
                                            <div className="text-[11px] text-ink-muted mt-0.5 inline-flex items-center gap-1">
                                                <Instagram size={10} />@{m.instagram}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => copy(m.phone, "Phone copied")}
                                        title="Copy phone"
                                        data-testid={`dir-copy-${m.id}`}
                                        className="inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-bg-surface/80 border border-emerald-line/60 hover:border-gold/50 hover:text-gold text-sm font-mono tracking-wide"
                                    >
                                        <Phone size={12} className="opacity-60" />
                                        <span>{m.phone || "—"}</span>
                                        <Copy size={11} className="opacity-40" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </main>
        </div>
    );
}
